import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { WarningCircle, Clock, X } from '@phosphor-icons/react';

// Slim app-wide banner shown on every page for two subscription states:
//   • EXPIRED (red)        — paid window has lapsed, premium features locked.
//   • EXPIRING SOON (amber) — valid but ends within 7 days; nudge to renew
//                             before access is interrupted.
// Owners/admins get a "Renew now" action to the Billing page; other roles
// just see the notice (they can't manage billing).
export default function ExpiredBanner() {
  const { isExpired, isExpiringSoon, expiresInDays, periodEnd, status } = useSubscription();
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const show = isExpired || isExpiringSoon;
  if (!show || dismissed) return null;

  const canManageBilling = ['owner', 'admin'].includes(currentOrg?.role);
  const planLabel = status?.plan_tier === 'business'
    ? 'Business'
    : (status?.plan_tier || '').replace(/^\w/, c => c.toUpperCase());
  const endDate = periodEnd ? new Date(periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const dayText = expiresInDays === 0 ? 'today' : expiresInDays === 1 ? 'tomorrow' : `in ${expiresInDays} days`;

  const cfg = isExpired
    ? {
        wrap: 'bg-red-600',
        btn: 'bg-white text-red-700 hover:bg-red-50',
        icon: <WarningCircle size={16} weight="fill" className="flex-shrink-0" />,
        testid: 'subscription-expired-banner',
        message: (
          <>Your {planLabel ? `${planLabel} ` : ''}subscription has expired. Premium features are locked
            {canManageBilling ? ' — renew to restore full access.' : ' — ask a workspace admin to renew.'}</>
        ),
      }
    : {
        wrap: 'bg-amber-500',
        btn: 'bg-white text-amber-700 hover:bg-amber-50',
        icon: <Clock size={16} weight="fill" className="flex-shrink-0" />,
        testid: 'subscription-expiring-banner',
        message: (
          <>Your {planLabel ? `${planLabel} ` : ''}plan expires {dayText}{endDate ? ` (${endDate})` : ''}.
            {canManageBilling ? ' Renew now to avoid any interruption.' : ' Ask a workspace admin to renew.'}</>
        ),
      };

  return (
    <div
      data-testid={cfg.testid}
      className={`flex items-center gap-3 px-4 py-2 ${cfg.wrap} text-white text-[13px] font-medium`}
    >
      {cfg.icon}
      <span className="flex-1 min-w-0">{cfg.message}</span>
      {canManageBilling && (
        <button
          type="button"
          onClick={() => navigate('/billing?renew=1')}
          data-testid="expiry-banner-renew"
          className={`flex-shrink-0 h-7 px-3 rounded-md ${cfg.btn} text-[12px] font-bold transition-colors`}
        >
          Renew now
        </button>
      )}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        data-testid="expiry-banner-dismiss"
        title="Dismiss"
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
